import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { SUPPORTED_LANGS } from "@/i18n";
import { asset } from "@/lib/asset";

// Verified Forwaro contact data (mirrors Footer.tsx)
const FORWARO_TEL = "+86 769 8558 1169";
const FORWARO_EMAIL = "pro@forwaro.com";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll while mobile menu is open, close on route change.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { key: "nav.home", to: "/" },
    { key: "nav.products", to: "/products" },
    { key: "nav.factory", to: "/factory" },
    { key: "nav.about", to: "/about" },
    { key: "nav.contact", to: "/contact" },
  ];

  const isTransparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 start-0 end-0 z-50 transition-all duration-300 ${
        isTransparent
          ? "bg-transparent"
          : "bg-white border-b border-stone-100 shadow-sm"
      }`}
    >
      <div className={`max-w-screen-xl mx-auto px-6 flex items-center justify-between transition-all duration-200 ${scrolled ? "h-14 md:h-20" : "h-20"}`}>
        {/* Logo — authentic Forwaro brand mark restored from the legacy site. */}
        <Link
          to="/"
          aria-label={t("brand.name")}
          className={`flex items-center gap-2.5 cursor-pointer shrink-0 transition-colors duration-300 ${
            isTransparent ? "text-white" : "text-stone-800"
          }`}
        >
          <img
            src={isTransparent ? asset("/brand/forwaro-mark-white.png") : asset("/brand/forwaro-mark-dark.png")}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 shrink-0 object-contain"
          />
          <span className="flex flex-col leading-tight whitespace-nowrap">
            <span
              className="font-bold text-xs md:text-[13px]"
              style={{ letterSpacing: "0.1em" }}
            >
              FORWARO FURNITURE.
            </span>
            <span
              className="hidden xl:block text-[10px] opacity-75 mt-0.5"
              style={{ letterSpacing: "0.2em" }}
            >
              風華國際 · 現代傢俱
            </span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.key}
                to={link.to}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? isTransparent
                      ? "text-white border-b border-white pb-0.5"
                      : "text-amber-700 border-b border-amber-700 pb-0.5"
                    : isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                {t(link.key)}
              </Link>
            );
          })}
        </nav>

        {/* Right side: Language switcher + CTA */}
        <div className="hidden md:flex items-center gap-5">
          <LanguageSwitcher variant={isTransparent ? "light" : "dark"} />
          <Link
            to="/contact?intent=quote"
            className="bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {t("nav.requestQuote")}
          </Link>
        </div>

        {/* Mobile Hamburger — morphs into X when open */}
        <button
          className="md:hidden relative z-[60] w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={t("nav.menu")}
          aria-expanded={mobileOpen}
        >
          {(() => {
            const color = mobileOpen ? "bg-white" : isTransparent ? "bg-white" : "bg-stone-800";
            return (
              <>
                <span
                  className={`block w-6 h-0.5 ${color} transition-all duration-300 ${
                    mobileOpen ? "translate-y-2 rotate-45" : ""
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 ${color} transition-all duration-200 ${
                    mobileOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 ${color} transition-all duration-300 ${
                    mobileOpen ? "-translate-y-2 -rotate-45" : ""
                  }`}
                />
              </>
            );
          })()}
        </button>
      </div>

      {/* Mobile Menu — full-screen premium overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-stone-950 transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Subtle backdrop — amber-tinted warmth */}
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950 via-stone-900 to-[#1a0f0a]" />
        <div
          className="absolute top-0 right-0 w-[80vw] h-[40vh] opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top right, rgba(180,83,9,0.15), transparent 60%)",
            filter: "blur(40px)",
          }}
        />

        <div className="relative h-full flex flex-col pt-24 pb-10 px-8 overflow-y-auto">
          {/* Primary nav */}
          <nav className="flex flex-col gap-1">
            {navLinks.map((link, i) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.key}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="group flex items-center justify-between py-3 border-b border-stone-800/60 transition-all duration-500 ease-out"
                  style={{
                    transitionDelay: mobileOpen ? `${i * 40 + 60}ms` : "0ms",
                    transform: mobileOpen ? "translateX(0)" : "translateX(16px)",
                    opacity: mobileOpen ? 1 : 0,
                  }}
                >
                  <span
                    className={`text-2xl font-semibold tracking-wide transition-colors ${
                      isActive ? "text-amber-400" : "text-white group-hover:text-amber-300"
                    }`}
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {t(link.key)}
                  </span>
                  <i
                    className={`ri-arrow-right-line text-xl transition-all ${
                      isActive ? "text-amber-400 translate-x-1" : "text-stone-600 group-hover:text-amber-400 group-hover:translate-x-1"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex-1" />

          {/* Language switcher — inline chip grid (no dropdown) so it never
              overflows the viewport inside the mobile overlay. */}
          <div
            className="pt-8 pb-4 transition-all duration-500"
            style={{
              transitionDelay: mobileOpen ? `${navLinks.length * 40 + 100}ms` : "0ms",
              opacity: mobileOpen ? 1 : 0,
            }}
          >
            <p className="text-stone-500 text-[10px] tracking-widest uppercase mb-3">
              {t("nav.language")}
            </p>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_LANGS.map((l) => {
                const isActive = l.code === i18n.language;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage(l.code);
                      setMobileOpen(false);
                    }}
                    lang={l.code}
                    dir={l.dir}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "bg-amber-500/20 border-amber-400 text-amber-300"
                        : "border-stone-700 text-stone-300 hover:border-stone-500"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick contact row — differentiated from the primary CTAs elsewhere */}
          <div
            className="pt-6 border-t border-stone-800/60 transition-all duration-500"
            style={{
              transitionDelay: mobileOpen ? `${navLinks.length * 40 + 160}ms` : "0ms",
              opacity: mobileOpen ? 1 : 0,
            }}
          >
            <p className="text-stone-500 text-[10px] tracking-widest uppercase mb-3">
              {t("footer.getInTouch.kicker")}
            </p>
            <div className="grid grid-cols-3 gap-2">
              <a
                href={`tel:${FORWARO_TEL.replace(/[^0-9+]/g, "")}`}
                onClick={() => setMobileOpen(false)}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-stone-800 hover:border-amber-700 transition-colors"
              >
                <i className="ri-phone-line text-amber-500 text-lg" />
                <span className="text-white/80 text-[11px]">{t("footer.contact.phone")}</span>
              </a>
              <a
                href={`mailto:${FORWARO_EMAIL}`}
                onClick={() => setMobileOpen(false)}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-stone-800 hover:border-amber-700 transition-colors"
              >
                <i className="ri-mail-line text-amber-500 text-lg" />
                <span className="text-white/80 text-[11px]">{t("footer.contact.email")}</span>
              </a>
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-green-900/50 bg-green-900/10 hover:border-green-600/60 transition-colors"
              >
                <i className="ri-whatsapp-line text-green-400 text-lg" />
                <span className="text-white/80 text-[11px]">WhatsApp</span>
              </Link>
            </div>
          </div>

          {/* Signature footer */}
          <p className="mt-6 text-center text-stone-600 text-[10px] tracking-widest uppercase">
            © Forwaro {new Date().getFullYear()} · 風華國際
          </p>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
